import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { approveStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/approve';
import { fetchStockReceiptDetail } from '#/api/imsMgmt/receipts/stockReceipt/fetchDetail';
import { rejectStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/reject';
import { submitStockReceipt } from '#/api/imsMgmt/receipts/stockReceipt/submit';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import {
  AppDialog,
  useConfirmDialog,
} from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  canApproveStockReceipt,
  canRejectStockReceipt,
  canSubmitStockReceipt,
} from '#/modules/imsMgmt/receipts/shared/receiptStatus';
import {
  getStockReceiptApiErrorMessage,
  isStockReceiptVersionConflict,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/apiError';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';
import { toast } from '#/utils/toast';
import {
  purchaseOrderStockReceiptListQueryKeyPrefix,
  stockReceiptDetailQueryKey,
} from '#/utils/queryKeys';

import useDetailTable from '#/hooks/table/useDetailTable';

function pickUpdatedAt(detailLike) {
  return detailLike?.record?.updatedAt ?? detailLike?.updatedAt ?? null;
}

function dash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function SummaryCell({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
        {dash(value)}
      </p>
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function mapReceiptLineRows(lines) {
  const rows = [];
  (lines ?? []).forEach((line, lineIndex) => {
    const mtr = line?.mtr ?? line?.material;
    const mtrNo = mtr?.mtrNo ?? mtr?.materialNo ?? '—';
    const name = mtr?.name ?? '—';
    const packages = line?.packages ?? [];
    if (packages.length === 0) {
      rows.push({
        id: `receipt-line-${lineIndex}`,
        stockNo: null,
        mtrNo,
        name,
        qty: '—',
        unit: '—',
      });
      return;
    }
    packages.forEach((pkg, pkgIndex) => {
      rows.push({
        id: `receipt-line-${lineIndex}-pkg-${pkgIndex}`,
        stockNo: pkg?.invStock?.stockNo ?? pkg?.stockNo ?? null,
        mtrNo,
        name,
        qty: pkg?.qty ?? pkg?.quantity ?? '—',
        unit: pkg?.unit?.name ?? pkg?.unit?.code ?? '—',
      });
    });
  });
  return rows;
}

function buildLineColumns(detailPath) {
  return [
    {
      id: 'stockNo',
      header: '庫存編號',
      accessorKey: 'stockNo',
      size: 150,
      minSize: 120,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => {
        const stockNo = row.original.stockNo;
        if (!stockNo) {
          return (
            <span className="text-muted-foreground">尚未建立庫存編號</span>
          );
        }
        return (
          <span className="font-mono">{stockNo}</span>
        );
      },
    },
    {
      id: 'mtrNo',
      header: '料號',
      accessorKey: 'mtrNo',
      size: 140,
      minSize: 100,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={row.original.mtrNo}
          detailPath={detailPath}
        />
      ),
    },
    {
      id: 'name',
      header: '名稱',
      accessorKey: 'name',
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={row.original.name}
          detailPath={detailPath}
        />
      ),
    },
    {
      id: 'qty',
      header: '數量',
      accessorFn: (row) => formatDisplayQty(row.qty),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'unit',
      header: '單位',
      accessorKey: 'unit',
      size: 90,
      enableSorting: false,
    },
  ];
}

/**
 * 採購單入庫歷史預覽：自寫摘要／明細／Footer（不嵌 MainInfoCard；不掛 WO Dialog）。
 * Footer 走 IMS stock-receipt submit／approve／reject。
 */
const PurchaseOrderStockReceiptPreviewDialog = ({
  open,
  onOpenChange,
  purchaseOrderId,
  receiptId,
  onMutated,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectOpen, setRejectOpen] = useState(false);

  const detailQueryKey = stockReceiptDetailQueryKey(receiptId);

  const detailQuery = useQuery({
    queryKey: detailQueryKey,
    queryFn: () => fetchStockReceiptDetail(receiptId),
    enabled: Boolean(open && receiptId),
  });

  const detail = detailQuery.data?.data;
  const status = detail?.status;
  const docNo = detail?.receiptNo ?? detail?.documentNo ?? '—';
  const description = docNo && docNo !== '—'
    ? (
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-medium">{docNo}</span>
        {status ? <DocumentStatusBadge status={status} size="sm" /> : null}
      </div>
    )
    : undefined;

  const canSubmit = canSubmitStockReceipt(status);
  const canApprove = canApproveStockReceipt(status);
  const canReject = canRejectStockReceipt(status);
  const detailPath = receiptId ? ROUTES.stockReceiptDetail(receiptId) : null;

  const lineRows = useMemo(
    () => (detail ? mapReceiptLineRows(detail.lines) : []),
    [detail],
  );

  const lineColumns = useMemo(
    () => buildLineColumns(detailPath),
    [detailPath],
  );

  const detailTableState = useDetailTable(lineRows, lineColumns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  const mutation = useMutation({
    mutationFn: async ({ action, rejectReason }) => {
      if (!receiptId) {
        throw new Error('缺少入庫單 id');
      }
      const updatedAt = pickUpdatedAt(detail);
      if (!updatedAt) {
        throw new Error('缺少更新時間，請重新載入後再試');
      }

      if (action === 'submit') {
        return submitStockReceipt(receiptId, { updatedAt });
      }
      if (action === 'approve') {
        return approveStockReceipt(receiptId, { updatedAt });
      }
      if (action === 'reject') {
        return rejectStockReceipt(receiptId, { updatedAt, rejectReason });
      }

      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectOpen(false);
      }
      if (purchaseOrderId) {
        await queryClient.invalidateQueries({
          queryKey: purchaseOrderStockReceiptListQueryKeyPrefix(purchaseOrderId),
        });
      }
      await queryClient.invalidateQueries({ queryKey: detailQueryKey });
      await onMutated?.();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(getStockReceiptApiErrorMessage(error));
      if (isStockReceiptVersionConflict(error)) {
        detailQuery.refetch();
      }
    },
  });

  const isPending = mutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交入庫單',
      message: '提交後將進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'submit' });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准入庫單',
      message: '核准後將入帳庫存，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'approve' });
  }

  function handleRejectConfirm(rejectReason) {
    mutation.mutate({ action: 'reject', rejectReason });
  }

  function handleClose() {
    if (isPending) {
      return;
    }
    onOpenChange(false);
  }

  const warehouseLabel = detail?.warehouse?.name
    ?? detail?.warehouse?.displayName
    ?? ([detail?.warehouse?.code, detail?.warehouse?.name].filter(Boolean).join(' ') || '—');

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={handleClose}
      >
        關閉
      </Button>
      {canReject ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending || detailQuery.isLoading}
          onClick={() => setRejectOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canApprove ? (
        <Button
          type="button"
          disabled={isPending || detailQuery.isLoading}
          onClick={handleApprove}
        >
          核准
        </Button>
      ) : null}
      {canSubmit ? (
        <Button
          type="button"
          disabled={isPending || detailQuery.isLoading}
          onClick={handleSubmit}
        >
          提交
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回入庫單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <AppDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isPending) {
            return;
          }
          onOpenChange(nextOpen);
        }}
        title="入庫單預覽"
        description={description}
        headerVariant="primary"
        showCloseButton
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={!isPending}
        closeOnEscape={!isPending}
        footer={footer}
      >
        {detailQuery.isLoading ? (
          <p className="py-6 text-sm text-muted-foreground">載入中…</p>
        ) : null}
        {detailQuery.isError ? (
          <p className="py-6 text-sm text-destructive">
            {getStockReceiptApiErrorMessage(detailQuery.error, '載入入庫單失敗')}
          </p>
        ) : null}
        {detail && !detailQuery.isLoading ? (
          <div className="space-y-4">
            <div
              className={cn(
                'grid grid-cols-1 gap-x-4 gap-y-3',
                'sm:grid-cols-2',
              )}
            >
              <SummaryCell
                label="日期"
                value={formatDateOrDash(detail.receiptDate)}
              />
              <SummaryCell label="倉庫" value={warehouseLabel} />
              <SummaryCell label="事由" value={detail.issueReason} />
              <SummaryCell label="備註" value={detail.remark} />
            </div>
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">明細</p>
              {lineRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">尚無明細</p>
              ) : (
                <DetailTable
                  state={detailTableState}
                  containerClassName="max-h-72 max-w-full overflow-auto"
                  tableClassName="detail-table--flat-lines"
                />
              )}
            </div>
          </div>
        ) : null}
      </AppDialog>
    </>
  );
};

PurchaseOrderStockReceiptPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  purchaseOrderId: PropTypes.string,
  receiptId: PropTypes.string.isRequired,
  onMutated: PropTypes.func,
};

export default PurchaseOrderStockReceiptPreviewDialog;
